import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Checkbox from "@mui/material/Checkbox";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import TableHeader from "components/Table/TableHeader";
import TableElement from "components/Table/TableElement";
import AutoComplete from "components/AutoComplete";
import { showToast } from "utils";
import { getInitialSlabForm } from "./constants";

const SlabRow = React.memo(({ slab, slabIndex, productIndex, setPayload, isLast, payoutCriteriaType }) => {
  const handleSlabChange = (field, value) => {
    setPayload((prev) => {
      const updated = [...prev.productsDTO];
      const slabs = [...updated[productIndex].slabs];
      slabs[slabIndex] = { ...slabs[slabIndex], [field]: value };
      updated[productIndex] = { ...updated[productIndex], slabs };
      return { ...prev, productsDTO: updated };
    });
  };

  const removeSlab = () => {
    setPayload((prev) => {
      const updated = [...prev.productsDTO];
      const slabs = updated[productIndex].slabs.filter((_, i) => i !== slabIndex);
      updated[productIndex] = { ...updated[productIndex], slabs };
      return { ...prev, productsDTO: updated };
    });
  };

  return (
    <tr className="divide-x divide-gray-200 hover:bg-slate-50">
      <TableElement value={`${productIndex + 1}.${slabIndex + 1}`} />
      <TableElement
        value={
          <Checkbox
            size="small"
            checked={slab.isDefaultSlab || false}
            onChange={(e) => handleSlabChange("isDefaultSlab", e.target.checked)}
          />
        }
      />
      <TableElement
        value={
          <TextField
            size="small"
            variant="outlined"
            value={slab.amount || ""}
            onChange={(e) => handleSlabChange("amount", e.target.value)}
            placeholder={payoutCriteriaType === "PERCENTAGE_PAY" ? "Percentage" : "Amount"}
            sx={{ minWidth: 80 }}
          />
        }
      />
      {payoutCriteriaType === "GIFT_REWARD_TRIP" && (
        <TableElement
          value={
            <TextField
              size="small"
              variant="outlined"
              value={slab.gift || ""}
              onChange={(e) => handleSlabChange("gift", e.target.value)}
              placeholder="Gift"
              sx={{ minWidth: 80 }}
            />
          }
        />
      )}
      <TableElement
        value={
          <TextField
            size="small"
            type="number"
            variant="outlined"
            value={slab.rangeFrom || ""}
            onChange={(e) => handleSlabChange("rangeFrom", parseInt(e.target.value) || 0)}
            placeholder="From"
            sx={{ minWidth: 70 }}
          />
        }
      />
      <TableElement
        value={
          <TextField
            size="small"
            type="number"
            variant="outlined"
            value={slab.rangeTo || ""}
            onChange={(e) => handleSlabChange("rangeTo", parseInt(e.target.value) || 0)}
            placeholder="To"
            sx={{ minWidth: 70 }}
          />
        }
      />
      <TableElement
        value={
          <TextField
            size="small"
            type="date"
            variant="outlined"
            value={slab.periodFrom || ""}
            onChange={(e) => handleSlabChange("periodFrom", e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 130 }}
          />
        }
      />
      <TableElement
        value={
          <TextField
            size="small"
            type="date"
            variant="outlined"
            value={slab.periodTo || ""}
            onChange={(e) => handleSlabChange("periodTo", e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 130 }}
          />
        }
      />
      <TableElement
        value={
          <TextField
            size="small"
            variant="outlined"
            value={slab.description || ""}
            onChange={(e) => handleSlabChange("description", e.target.value)}
            placeholder="Description"
            sx={{ minWidth: 100 }}
          />
        }
      />
      <TableElement
        value={
          <IconButton size="small" color="error" onClick={removeSlab}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        }
      />
    </tr>
  );
});

const ProductSlabSection = React.memo(({ product, productIndex, setPayload, payoutCriteriaType, periodFrom, periodTo, fetchProducts }) => {
  const [isExpanded, setIsExpanded] = React.useState(productIndex === 0);
  const [isEditingProduct, setIsEditingProduct] = React.useState(false);
  const isUnknown = !product.productName || product.productName === "Unknown Product" || product.productName.trim() === "";
  
  const handleProductCodeChange = (value) => {
    setPayload((prev) => {
      const updated = [...prev.productsDTO];
      updated[productIndex] = { ...updated[productIndex], productCode: value };
      return { ...prev, productsDTO: updated };
    });
  };

  const handleProductSelection = (_, value) => {
    if (!value || value.length === 0) return;
    const selected = value[0]; // AutoComplete passes an array since multiple=true by default is passed from AddSchemeModal

    setPayload((prev) => {
      const updated = [...prev.productsDTO];
      updated[productIndex] = {
        ...updated[productIndex],
        productId: selected.id,
        productName: `${selected.primaryName || ""} ${selected.secondaryName || ""}`.trim(),
        productCode: selected.productCode || updated[productIndex].productCode,
      };

      // Update the global products array to reflect the change
      const globalProducts = [...prev.products];
      const existingIndex = globalProducts.findIndex(p => p.id === product.productId);

      if (existingIndex !== -1) {
        // Update existing product
        globalProducts[existingIndex] = selected;
      } else {
        // Add new product if not found
        globalProducts.push(selected);
      }

      return { ...prev, productsDTO: updated, products: globalProducts };
    });
    setIsEditingProduct(false);
  };

  const addNewSlab = () => {
    const existingSlabs = product.slabs || [];
    const initialForm = getInitialSlabForm(periodFrom, periodTo);

    let newSlab = { ...initialForm };

    if (existingSlabs.length > 0) {
      const firstSlab = existingSlabs[0];
      const lastSlab = existingSlabs[existingSlabs.length - 1];

      const firstRangeFrom = parseInt(firstSlab.rangeFrom) || 0;
      const firstRangeTo = parseInt(firstSlab.rangeTo) || 0;
      const rangeDiff = firstRangeTo - firstRangeFrom;

      const lastRangeTo = parseInt(lastSlab.rangeTo) || 0;
      const newRangeFrom = lastRangeTo + 1;
      const newRangeTo = newRangeFrom + rangeDiff;

      newSlab = {
        ...initialForm,
        rangeFrom: newRangeFrom,
        rangeTo: newRangeTo,
      };
    }

    setPayload((prev) => {
      const updated = [...prev.productsDTO];
      const slabs = [...(updated[productIndex].slabs || []), newSlab];
      updated[productIndex] = { ...updated[productIndex], slabs };
      return { ...prev, productsDTO: updated };
    });
  };

  return (
    <>
      <tr className="bg-blue-50">
        <td colSpan={10} className="px-4 py-2">
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
              <IconButton size="small" onClick={() => setIsExpanded(!isExpanded)}>
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              {!isUnknown && !isEditingProduct ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {productIndex + 1}. {product.productName} ({product.slabs?.length || 0} slabs)
                  </Typography>
                  <IconButton size="small" onClick={() => setIsEditingProduct(true)}>
                    <EditIcon fontSize="small" color="primary" />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ minWidth: 250, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'error.main' }}>
                    {productIndex + 1}.
                  </Typography>
                  <AutoComplete
                    sx={{ flex: 1 }}
                    label={isUnknown ? "Search Missing Product" : "Change Product"}
                    multiple={true} // AutoComplete is designed for multiple items universally but here we limit selection logic
                    getOptionLabel={(option) => `${option.primaryName || ""} ${option.secondaryName || ""}`.trim()}
                    value={[]}
                    fetchOptions={fetchProducts}
                    onChange={(e, val) => handleProductSelection(e, val)}
                  />
                  {!isUnknown && (
                    <IconButton size="small" onClick={() => setIsEditingProduct(false)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}
              <TextField
                size="small"
                variant="outlined"
                label="Product Code"
                value={(() => {
                  // For published schemes: use product.product.productCode
                  // For draft/extracted: use product.productCode
                  const code = product.product?.productCode || product.productCode || "";
                  const firstUnderscore = code.indexOf("_");
                  return firstUnderscore !== -1 ? code.slice(firstUnderscore + 1) : code;
                })()}
                onChange={(e) => {
                  // Reconstruct full code: brand prefix + underscore + new value
                  const code = product.product?.productCode || product.productCode || "";
                  const firstUnderscore = code.indexOf("_");
                  const prefix = firstUnderscore !== -1 ? code.slice(0, firstUnderscore + 1) : "";
                  handleProductCodeChange(prefix + e.target.value);
                }}
                placeholder="Enter product code"
                sx={{ minWidth: 260 }}
              />
            </Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setIsExpanded(true);
                addNewSlab();
              }}
              variant="contained"
              color="primary"
              sx={{ textTransform: "none" }}
            >
              Add Slab
            </Button>
          </Box>
        </td>
      </tr>
      {isExpanded && product.slabs?.map((slab, slabIndex) => (
        <SlabRow
          key={slabIndex}
          slab={slab}
          slabIndex={slabIndex}
          productIndex={productIndex}
          setPayload={setPayload}
          isLast={slabIndex === product.slabs.length - 1}
          payoutCriteriaType={payoutCriteriaType}
        />
      ))}
      {isExpanded && (!product.slabs || product.slabs.length === 0) && (
        <tr>
          <td colSpan={10} className="px-4 py-3 text-center text-gray-500">
            No slabs added. Click "Add Slab" to add one.
          </td>
        </tr>
      )}
    </>
  );
});

const ProductsSlabTable = ({ payload, setPayload, fetchProducts }) => {
  const applySlabToAll = () => {
    if (!payload.productsDTO?.length) return;

    const firstSlabs = payload.productsDTO[0]?.slabs || [];
    if (!firstSlabs.length) {
      showToast("First product has no slabs to apply");
      return;
    }

    const updated = payload.productsDTO.map((product, idx) => {
      if (idx === 0) return product;
      return { ...product, slabs: firstSlabs.map((slab) => ({ ...slab })) };
    });

    setPayload((prev) => ({ ...prev, productsDTO: updated }));
    showToast("Slabs applied to all products", "success");
  };

  if (!payload.productsDTO?.length) {
    return null;
  }

  return (
    <div className="mt-5">
      {payload.productsDTO?.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <Button variant="outlined" onClick={applySlabToAll}>
            Apply First Product Slabs to All
          </Button>
        </Box>
      )}

      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-100">
          <tr className="divide-x divide-gray-200">
            <TableHeader heading="#" />
            <TableHeader heading="Default" />
            <TableHeader heading={payload.schemeConfigration?.payoutCriteriaType === "PERCENTAGE_PAY" ? "Percentage" : "Amount"} />
            {payload.schemeConfigration?.payoutCriteriaType === "GIFT_REWARD_TRIP" && (
              <TableHeader heading="Gift" />
            )}
            <TableHeader heading="Range From" />
            <TableHeader heading="Range To" />
            <TableHeader heading="Period From" />
            <TableHeader heading="Period To" />
            <TableHeader heading="Description" />
            <TableHeader heading="" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {payload.productsDTO?.map((product, i) => (
            <ProductSlabSection
              key={product.productId || i}
              product={product}
              productIndex={i}
              setPayload={setPayload}
              fetchProducts={fetchProducts}
              payoutCriteriaType={payload.schemeConfigration?.payoutCriteriaType}
              periodFrom={payload.periodFrom}
              periodTo={payload.periodTo}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ProductsSlabTable;
